/* ============================================================
   TASK-331 (owner bug report, staging 2026-09-07) — plan switching during a
   trial. On a trial (card on file, not charged yet) every other plan card on
   /app/plans used to be disabled with "Available after your trial activates
   — or cancel and resubscribe" — the checkout endpoint can't be called
   (maybeConvertExistingSubscription acts on the EXISTING subscription and
   never reads the requested plan), so the only reachable path was change-
   plan itself. stripe-worker's change-plan.ts now supports it directly:
   D1 — AI-plan-to-AI-plan while trialing carries proration_behavior:'none'
   (no charge now, the new plan is billed at trial end); D3 — switching to
   Scheduler ends the trial now (trial_end:'now') since it never has one;
   D2 — either switch also clears a pending cancel_at_period_end.

   These are the pure decision/copy helpers Plans.jsx routes through, pulled
   out of the screen so they're unit-testable without a React render harness
   — this repo's vitest has no jsdom/@testing-library configured (see
   package.json), so every screen-level branching decision with real logic
   lives in lib/ for exactly this reason (see lib/planTracks.js's
   resolveSwitchLabel for the established pattern this file follows).
   ============================================================ */

/**
 * TASK-331 phase 2 (Subscription Schedules, design brief 2026-09-07) — pure
 * read of the entitlements payload's scheduled-downgrade pair. Both fields
 * are only ever written together (scheduleDeferredDowngrade in
 * change-plan.ts) and cleared together (release-scheduled-plan-change.ts /
 * the webhook's plan-flip promotion) — this guards the half-set case too
 * rather than trusting that invariant blindly, so a caller never renders a
 * "Switching to undefined on ..." notice off a partially-mirrored response.
 *
 * @param {object} entitlements - GET /api/billing/entitlements payload (or
 *   its unwrapped `.data`).
 * @returns {{ plan: string, effectiveAt: string } | null}
 */
export function resolveScheduledDowngrade(entitlements) {
  const plan = entitlements?.scheduledPlan ?? null;
  const effectiveAt = entitlements?.scheduledPlanEffectiveAt ?? null;
  if (!plan || !effectiveAt) return null;
  return { plan, effectiveAt };
}

/**
 * Whether a non-current plan card should route through the in-place
 * change-plan endpoint ("switch") instead of the checkout endpoint.
 * D1/D3: a trialing org can now switch directly. `hasLiveSub` keeps its
 * existing meaning (active/checkout_completed/past_due, non-lapsed) — the
 * checkout guard everywhere else in Plans.jsx is unchanged by this.
 */
export function canSwitchPlanInPlace({ isTrialing, hasLiveSub }) {
  return !!isTrialing || !!hasLiveSub;
}

/**
 * Should the "your trial ends on <date> and won't renew" line render at the
 * top of the grid? D5: only while trialing, only once cancel_at_period_end
 * is set, and only on the new (two-track) grid this whole card design lives
 * on.
 */
export function shouldShowCancelSchedulePick({ newGridActive, isTrialing, cancelAtPeriodEnd }) {
  return !!newGridActive && !!isTrialing && !!cancelAtPeriodEnd;
}

/**
 * Post-switch confirmation copy. Read straight from change-plan's own
 * additive response fields (requiresAction / chargedNow / noChange /
 * trialPreserved / cancellationCleared) — never re-derived from
 * billingStatus, which the webhook mirror can still be catching up to even
 * though the switch call itself already succeeded.
 *
 * Always returns `{ requiresAction, message, hostedInvoiceUrl }` (never a
 * bare string) so the caller can never accidentally treat a requiresAction
 * outcome as a completed switch — the screen branches on the flag, not on
 * string-matching the message.
 *
 * Branch order (highest priority first): requiresAction (the switch has NOT
 * taken effect — nothing else below is even relevant yet) > chargedNow (the
 * trial-ended-now Scheduler path, TASK-331 D3/BLOCKER 1) > noChange (already
 * on this plan, MINOR 3) > the pre-existing brand-activation nudge >
 * trialPreserved (D1) > the plain prorated-switch fallback.
 *
 * @param {object} params
 * @param {boolean} [params.requiresAction] - change-plan's prorated/first
 *   invoice needs the customer (3DS, a declined card); the plan has NOT
 *   changed yet.
 * @param {string|null} [params.hostedInvoiceUrl] - Stripe's hosted invoice
 *   page, present whenever requiresAction is true.
 * @param {boolean} [params.chargedNow] - a trial->Scheduler switch (D3) paid
 *   its first invoice immediately.
 * @param {number|null} [params.amountPaidCents]
 * @param {string} [params.currency='EUR']
 * @param {(cents: number, currency: string) => string|null} [params.formatMoney]
 *   - injected money formatter (e.g. formatPostingMoney bound to a locale) so
 *   this stays a pure, dependency-free function; omitting it (or an
 *   unformattable `amountPaidCents`) falls back to plansPage.switchChargedNowNoAmount
 *   instead of interpolating an empty amount into the sentence.
 * @param {boolean} [params.noChange] - change-plan's "already on this plan"
 *   early return (MINOR 3).
 * @param {boolean} params.showActivationNotice - pre-existing
 *   planChangedActivation nudge ("finish your brand profile"); unrelated to
 *   trial state and takes priority over the trial/plain copy below.
 * @param {boolean} [params.trialPreserved]
 * @param {boolean} [params.settledWithoutCharge] - change-plan's invoice
 *   settled at exactly €0 (a 100%-off coupon, or a credit that fully covers
 *   the switch) — money moved through Stripe's books but nothing was
 *   actually charged. Slots in right after chargedNow (the two are mutually
 *   exclusive per classifyInvoiceCharge in change-plan.ts) and ahead of
 *   noChange/activation/trialPreserved: a settled invoice is a more specific,
 *   more useful fact than any of those. `invoiceUnknown` (no invoice data at
 *   all) intentionally has no branch of its own here — it already falls
 *   through to the plain fallback below, which makes no amount claim.
 *   MINOR 6: like noChange/trialPreserved, appends switchDoneCancelCleared
 *   when cancellationCleared is also true — a $0-settled switch can just as
 *   easily have cleared a pending cancel_at_period_end as any other switch.
 * @param {boolean} [params.cancellationCleared]
 * @param {string} params.planLabel
 * @param {boolean} [params.scheduled] - TASK-331 phase 2: change-plan's
 *   deferred-downgrade branch (Subscription Schedules) — the switch was
 *   ACCEPTED but has not taken effect yet: the org keeps its current plan
 *   until `effectiveAt`, then flips automatically. Highest priority (checked
 *   before requiresAction/chargedNow/etc.) since it is its own uniform
 *   response shape from a distinct early return in change-plan.ts
 *   (scheduleDeferredDowngrade) — none of the other outcome fields are ever
 *   set alongside it.
 * @param {string|null} [params.effectiveAt] - pre-formatted date string (the
 *   caller formats it, same 'en'-locale convention every other billing date
 *   on this page uses — see Plans.jsx's formatBillingDate).
 * @param {string} [params.currentPlanLabel] - the plan the org keeps until
 *   `effectiveAt` (i.e. its plan BEFORE this switch call).
 * @param {(key: string, vars?: object) => string} params.t
 */
export function resolveSwitchNotice({
  requiresAction,
  hostedInvoiceUrl,
  chargedNow,
  amountPaidCents,
  currency = 'EUR',
  formatMoney,
  noChange,
  showActivationNotice,
  trialPreserved,
  settledWithoutCharge,
  cancellationCleared,
  planLabel,
  scheduled,
  effectiveAt,
  currentPlanLabel,
  t,
}) {
  const translate = typeof t === 'function' ? t : (key) => key;
  if (scheduled) {
    return {
      requiresAction: false,
      message: translate('plansPage.planDowngradeScheduled', { plan: planLabel, date: effectiveAt, currentPlan: currentPlanLabel }),
      hostedInvoiceUrl: null,
    };
  }
  if (requiresAction) {
    return {
      requiresAction: true,
      message: translate('plansPage.switchRequiresAction', { plan: planLabel }),
      hostedInvoiceUrl: hostedInvoiceUrl || null,
    };
  }
  if (chargedNow) {
    const amount = typeof formatMoney === 'function' ? formatMoney(amountPaidCents, currency) : null;
    // Owner bug report (screenshot, staging 2026-09-07): this used to hardcode
    // "Switched to Scheduler" regardless of which plan was actually switched
    // to (Scheduler is the only plan D3's trial-ends-now charge applies to
    // TODAY, but the copy shouldn't assume that stays true forever) — now
    // names `planLabel` like every sibling branch below already does. And
    // when `amount` comes back empty (no formatMoney injected, or
    // amountPaidCents isn't a formattable integer — see formatPostingMoney)
    // fall through to a no-amount variant instead of interpolating an empty
    // string, which left a dangling "—  charged today." with nothing to
    // show for it. This is still a real, non-zero charge (unlike
    // settledWithoutCharge below), so the copy must not claim otherwise.
    const message = amount
      ? translate('plansPage.switchChargedNow', { plan: planLabel, amount })
      : translate('plansPage.switchChargedNowNoAmount', { plan: planLabel });
    return {
      requiresAction: false,
      message,
      hostedInvoiceUrl: null,
    };
  }
  if (settledWithoutCharge) {
    const base = translate('plansPage.switchSettledNoCharge', { plan: planLabel });
    return {
      requiresAction: false,
      message: cancellationCleared ? `${base} ${translate('plansPage.switchDoneCancelCleared')}` : base,
      hostedInvoiceUrl: null,
    };
  }
  if (noChange) {
    const base = translate('plansPage.switchNoChange', { plan: planLabel });
    return {
      requiresAction: false,
      message: cancellationCleared ? `${base} ${translate('plansPage.switchDoneCancelCleared')}` : base,
      hostedInvoiceUrl: null,
    };
  }
  if (showActivationNotice) {
    return { requiresAction: false, message: translate('plansPage.planChangedActivation'), hostedInvoiceUrl: null };
  }
  if (trialPreserved) {
    const base = translate('plansPage.switchDoneTrial', { plan: planLabel });
    return {
      requiresAction: false,
      message: cancellationCleared ? `${base} ${translate('plansPage.switchDoneCancelCleared')}` : base,
      hostedInvoiceUrl: null,
    };
  }
  return { requiresAction: false, message: translate('plansPage.planChanged'), hostedInvoiceUrl: null };
}

// TASK-331 (SPA follow-up, staging bug report 2026-09-07): the specific,
// machine-readable failure reasons change-plan's stripe-worker handler can
// throw (workers/core/dathent-stripe-worker/src/handlers/change-plan.ts).
// Every one of these arrives as a DathentAPIError with code
// 'VALIDATION_ERROR' and the actual sub-code in `err.details.reason` (the
// client merges `error.details` into `err.details` — see
// src/services/api/client.js ~:335-350). Anything else (a different code, or
// a VALIDATION_ERROR whose reason isn't one of these four) is intentionally
// NOT classified here — resolveSwitchError returns null and the caller falls
// back to resolveGenericSwitchError below, which now shows the localized
// generic copy for an unrecognized VALIDATION_ERROR reason (its raw message
// is developer/ops text, not customer copy) and only the raw `err.message`
// for anything else (no code at all — a network/unexpected error).
const SWITCH_ERROR_REASONS = new Set([
  'PAYMENT_METHOD_REQUIRED',
  'PAYMENT_METHOD_UNVERIFIED',
  'SUBSCRIPTION_ENDED',
  'SUBSCRIPTION_NOT_CHANGEABLE',
]);

/**
 * Classify a change-plan failure into copy + a CTA shape Plans.jsx can render
 * directly, instead of showing the raw server message verbatim. Pure and
 * dependency-free (like the rest of this file) so it's unit-testable without
 * constructing a real DathentAPIError.
 *
 * `cta` tells the caller which action to wire up (Plans.jsx owns the actual
 * navigate()/retry-handler/checkout-routing logic — this function only ever
 * decides copy + which kind of action applies):
 *   - 'billingSettings' — deep-link to Settings → Billing (PAYMENT_METHOD_
 *     REQUIRED: no card on file; SUBSCRIPTION_NOT_CHANGEABLE: pay the open
 *     invoice first). Reuse the same route paymentIssueLinks().billingSettings
 *     already resolves to, so this never drifts from PaymentIssueNotice's own
 *     link.
 *   - 'retry' — re-run the exact same switch (PAYMENT_METHOD_UNVERIFIED: the
 *     live payment-method check failed transiently, no state changed).
 *   - 'checkout' — no button of its own; the caller instead forces
 *     canSwitchPlanInPlace() false so every card's CTA routes through
 *     handleSubscribeNew (checkout) instead of change-plan for the rest of
 *     this page visit (SUBSCRIPTION_ENDED: the trial subscription is gone,
 *     change-plan would just 404 on a retry).
 *
 * @param {object} params
 * @param {string} [params.code] - err.code
 * @param {string} [params.reason] - err.details.reason
 * @param {string} [params.status] - err.details.status (past_due|incomplete|
 *   unpaid, only ever set for SUBSCRIPTION_NOT_CHANGEABLE). MINOR 3: accepted
 *   for future use but never rendered into the message — see the
 *   SUBSCRIPTION_NOT_CHANGEABLE branch below.
 * @param {(key: string, vars?: object) => string} params.t
 * @returns {{reason: string, message: string, ctaLabel: string|null, cta: 'billingSettings'|'retry'|'checkout'}|null}
 */
export function resolveSwitchError({ code, reason, status, t }) {
  const translate = typeof t === 'function' ? t : (key) => key;
  if (code !== 'VALIDATION_ERROR' || !SWITCH_ERROR_REASONS.has(reason)) return null;

  if (reason === 'PAYMENT_METHOD_REQUIRED') {
    return {
      reason,
      message: translate('plansPage.errPaymentMethodRequired'),
      ctaLabel: translate('plansPage.errPaymentMethodRequiredCta'),
      cta: 'billingSettings',
    };
  }
  if (reason === 'PAYMENT_METHOD_UNVERIFIED') {
    return {
      reason,
      message: translate('plansPage.errPaymentMethodUnverified'),
      ctaLabel: translate('plansPage.tryAgain'),
      cta: 'retry',
    };
  }
  if (reason === 'SUBSCRIPTION_ENDED') {
    return {
      reason,
      message: translate('plansPage.errSubscriptionEnded'),
      ctaLabel: null,
      cta: 'checkout',
    };
  }
  // SUBSCRIPTION_NOT_CHANGEABLE — MINOR 3: `status` (a raw Stripe/D1 status
  // word — past_due|incomplete|unpaid) used to be appended verbatim as a
  // dangling "(past_due)" parenthetical, which read as an untranslated
  // fragment bolted onto a full sentence. plansPage.errNotChangeable is
  // already a complete statement + instruction ("Pay your open invoice
  // first, then change plan") in every locale, so the raw word is dropped
  // from the user-facing message entirely — `status` is accepted for
  // logging/future use but never rendered.
  return {
    reason,
    message: translate('plansPage.errNotChangeable'),
    ctaLabel: translate('plansPage.errNotChangeableCta'),
    cta: 'billingSettings',
  };
}

// MINOR 8: Plans.jsx's handleSwitchPlan catch falls back to this when
// resolveSwitchError returns null (an unrecognized reason, or none at all).
// A VALIDATION_ERROR is still a machine-checked failure the server rejected
// on purpose (e.g. "no default price configured on the target plan") — its
// raw err.message is developer/ops text, not something a customer should
// read, so it's replaced with the generic checkout-error copy. Any other
// failure (no code at all — a network/unexpected error) keeps the
// pre-existing behaviour of preferring the raw message when present, since
// that's often the only actionable information available.
export function resolveGenericSwitchError({ code, message, t }) {
  const translate = typeof t === 'function' ? t : (key) => key;
  if (code === 'VALIDATION_ERROR') return translate('upgradeModal.checkoutError');
  return message || translate('upgradeModal.checkoutError');
}

/**
 * Refetch entitlements until `plan` matches the just-switched-to plan key,
 * or up to `maxRetries` retries (each preceded by `delayMs`) are exhausted
 * — the change-plan webhook mirror to D1 can trail this call's own success
 * response by a beat. `fetchEntitlements`/`invalidateCache`/`wait` are
 * injected so this stays a pure, fake-timer-testable function with no
 * import of the api client.
 *
 * TASK-331 (SPA follow-up, staging bug report 2026-09-08): every attempt
 * asks `fetchEntitlements` for a FRESH read (`{ fresh: true }`, which the
 * caller wires to api.billing.getEntitlements's `fresh` option → `?fresh=1`)
 * — the api-gateway memoises /api/billing/entitlements per org for ~60s, so
 * right after a switch a non-fresh read can keep echoing the pre-switch
 * plan (and its social-addon provenance line) for up to a minute even after
 * `invalidateCache` below has cleared this client's OWN cache. This helper
 * exists specifically to confirm a just-completed switch, so it always
 * wants the gateway's live state — unlike ordinary dashboard polling, which
 * must keep using the memoised read (see api.billing.getEntitlements).
 *
 * @returns the last entitlements payload seen (unwrapped from `.data` if
 *   present), or null if every fetch attempt failed.
 */
export async function refetchEntitlementsUntilPlanMatches({
  expectedPlanKey,
  fetchEntitlements,
  invalidateCache,
  wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
  maxRetries = 3,
  delayMs = 1500,
} = {}) {
  let entData = null;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await wait(delayMs);
      if (typeof invalidateCache === 'function') invalidateCache('/api/billing/entitlements');
    }
    // eslint-disable-next-line no-await-in-loop
    const ent = await fetchEntitlements({ fresh: true }).catch(() => null);
    if (ent) entData = ent?.data ?? ent;
    if (entData?.plan === expectedPlanKey) break;
  }
  return entData;
}
